import { EntityAvatar } from '@/modules/common';
import { ScrollableText } from '@/modules/planner/components/assignment/text/scrollable-text';

type AvatarSize = 'sm' | 'md' | 'lg' | 'xl' | 'xs';

interface ScrollableCellProps {
  /** Text content to display with scroll-on-hover */
  text: string;
  /** Optional className applied to the outer flex container */
  className?: string;
  /** Optional className applied to the ScrollableText wrapper (e.g. for responsive font size) */
  textClassName?: string;
  /** Optional style applied to the ScrollableText (e.g. for dynamic color) */
  textStyle?: React.CSSProperties;
}

interface ScrollableCellWithAvatarProps extends ScrollableCellProps {
  /** Image URL for the avatar */
  image?: string | File | null;
  /** Name for avatar fallback initials */
  avatarName: string;
  /** Avatar size */
  avatarSize?: AvatarSize;
  /** Alt text for the avatar image */
  alt?: string;
  /** Show a border around the fallback avatar when it needs separation from the surface. */
  withAvatarBorder?: boolean;
}

interface ScrollableCellWithColorDotProps extends ScrollableCellWithAvatarProps {
  /** Color for the dot indicator (e.g. project color) */
  dotColor?: string;
}

/**
 * Table cell with scrollable text that reveals full content on hover.
 * Use for plain text columns (email, department, role, etc.).
 */
export function ScrollableCell({ text, className, textStyle }: ScrollableCellProps) {
  return (
    <ScrollableText className={className} style={textStyle}>
      {text}
    </ScrollableText>
  );
}

/**
 * Table cell with avatar + scrollable text.
 * Use for entity columns (team member name, client name, manager, etc.).
 */
export function ScrollableCellWithAvatar({
  text,
  image,
  avatarName,
  avatarSize = 'sm',
  alt,
  withAvatarBorder = false,
  className,
  textStyle,
}: ScrollableCellWithAvatarProps) {
  return (
    <div className={`flex min-w-0 items-center gap-2 ${className ?? ''}`}>
      <div className="shrink-0">
        <EntityAvatar
          image={image}
          name={avatarName}
          size={avatarSize}
          shape="rounded"
          alt={alt}
          withBorder={withAvatarBorder}
        />
      </div>
      <div className="min-w-0 overflow-hidden">
        <ScrollableText className="whitespace-nowrap" style={textStyle}>
          {text}
        </ScrollableText>
      </div>
    </div>
  );
}

/**
 * Table cell with avatar + color dot + scrollable text.
 * Use for project name columns that show a color indicator.
 */
export function ScrollableCellWithColorDot({
  text,
  image,
  avatarName,
  avatarSize = 'sm',
  alt,
  dotColor,
  withAvatarBorder = false,
  className,
  textClassName,
  textStyle,
}: ScrollableCellWithColorDotProps) {
  return (
    <div className={`flex min-w-0 items-center gap-3 ${className ?? ''}`}>
      <div className="shrink-0">
        <EntityAvatar
          image={image}
          name={avatarName}
          size={avatarSize}
          shape="rounded"
          alt={alt}
          withBorder={withAvatarBorder}
        />
      </div>
      <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
        {dotColor && (
          <div className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: dotColor }} />
        )}
        <div className="min-w-0 overflow-hidden">
          <ScrollableText className={`whitespace-nowrap ${textClassName ?? ''}`} style={textStyle}>
            {text}
          </ScrollableText>
        </div>
      </div>
    </div>
  );
}
